export type ManagedPlanCode =
  | 'starter'
  | 'standard'
  | 'professional'
  | 'enterprise'
  | 'enterprise_premium';

export interface SubscriptionPlanConfig {
  code: ManagedPlanCode;
  label: string;
  monthlyPrice: number;
  /**
   * Optional label to override price displayed to end-users.
   * When provided, format helpers use this instead of numeric formatting.
   */
  displayPriceOverride?: string;
  /**
   * ユーザー数上限（無制限の場合は null）
   */
  userLimit: number | null;
  /**
   * 設備登録数上限（無制限の場合は null）
   * 設備登録数 = 健全度予測可能数
   */
  equipmentLimit: number | null;
  /**
   * ストレージ容量（GB単位、無制限の場合は null）
   */
  storageCapacityGB: number | null;
  /**
   * アーカイブ期間（日数）
   */
  archiveDays: number;
  featureHighlights: string[];
  shortDescription: string;
  scene: string;
  targetSummary: string;
  useCases: string[];
}

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return undefined;
};

const resolveEnterprisePremiumFlag = (): boolean => {
  const candidates: unknown[] = [];

  if (typeof process !== 'undefined' && process.env) {
    candidates.push(process.env.AVS_ENABLE_ENTERPRISE_PREMIUM);
    candidates.push(process.env.VITE_ENABLE_ENTERPRISE_PREMIUM);
  }

  if (typeof globalThis !== 'undefined') {
    candidates.push(
      (globalThis as Record<string, unknown>).AVS_ENABLE_ENTERPRISE_PREMIUM,
    );
  }

  for (const candidate of candidates) {
    const parsed = parseBoolean(candidate);
    if (typeof parsed === 'boolean') {
      return parsed;
    }
  }

  return false;
};

export const ENTERPRISE_PREMIUM_ENABLED = resolveEnterprisePremiumFlag();

export const MANAGED_PLAN_ENUM = ENTERPRISE_PREMIUM_ENABLED
  ? (['starter', 'standard', 'professional', 'enterprise', 'enterprise_premium'] as const)
  : (['starter', 'standard', 'professional', 'enterprise'] as const);

export const DEFAULT_TAX_RATE = 0.1;

const currencyFormatter = new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
  maximumFractionDigits: 0,
});

const yenNumberFormatter = new Intl.NumberFormat('ja-JP', {
  maximumFractionDigits: 0,
});

const formatYen = (amount: number): string => `${yenNumberFormatter.format(amount)}円`;

const calculateTaxIncludedAmount = (amount: number, taxRate: number = DEFAULT_TAX_RATE): number =>
  Math.round(amount * (1 + taxRate));

export const formatMonthlyAmountWithTax = (
  amount: number,
  options: { withSuffix?: boolean; taxRate?: number } = {},
): string => {
  const taxRate = options.taxRate ?? DEFAULT_TAX_RATE;
  const taxIncluded = calculateTaxIncludedAmount(amount, taxRate);
  const suffix = options.withSuffix ? '/月' : '';
  const taxLabelPrefix = options.withSuffix ? '税込' : '税込価格';
  const separator = options.withSuffix ? ' ' : '';

  const base = `${formatYen(amount)}${suffix}`;
  const taxDisplay = `${taxLabelPrefix}${formatYen(taxIncluded)}${suffix}`;

  return `${base}${separator}(${taxDisplay})`;
};

export const MANAGED_PLAN_ORDER: ManagedPlanCode[] = [...MANAGED_PLAN_ENUM];

export type PublicManagedPlanCode = Exclude<ManagedPlanCode, 'enterprise_premium'>;

export const PUBLIC_PLAN_ORDER: PublicManagedPlanCode[] = MANAGED_PLAN_ORDER.filter(
  (code): code is PublicManagedPlanCode => code !== 'enterprise_premium',
);

export const SUBSCRIPTION_PLAN_CONFIGS: Record<
  ManagedPlanCode,
  SubscriptionPlanConfig
> = {
  starter: {
    code: 'starter',
    label: 'スターター',
    monthlyPrice: 19_800,  // 税抜価格
    userLimit: null,
    equipmentLimit: 50,
    storageCapacityGB: 20,
    archiveDays: 180,
    featureHighlights: [
      'ユーザー数 無制限',
      '設備登録数 50台まで（健全度予測も50台まで）',
      'ストレージ容量 20GB',
      'トライアル後の小規模導入に最適',
      '初月は日割り計算、翌月から月末定期請求',
    ],
    shortDescription: '試用導入向けのエントリープラン（初月日割り・月初請求）',
    scene: '試用導入',
    targetSummary: '対象: システム試用・小規模導入',
    useCases: [
      'トライアル期間後の本格導入前評価',
      'チーム全員での設備管理運用開始',
      '50台までの設備管理と20GBストレージ運用',
    ],
  },
  standard: {
    code: 'standard',
    label: 'スタンダード',
    monthlyPrice: 49_800,  // 税抜価格
    userLimit: null,
    equipmentLimit: 300,
    storageCapacityGB: 100,
    archiveDays: 365,
    featureHighlights: [
      'ユーザー数 無制限',
      '設備登録数 300台まで（健全度予測も300台まで）',
      'ストレージ容量 100GB',
      '小規模工場の設備管理に最適',
      '初月は日割り計算、翌月から月末定期請求',
    ],
    shortDescription: '小規模工場向けのベースプラン（初月日割り・月初請求）',
    scene: '小規模工場',
    targetSummary: '対象: 小規模工場',
    useCases: [
      '工場管理チーム全員での設備管理',
      '300台までの設備登録と管理',
      '主要設備20台の健全度予測・メンテナンス計画',
    ],
  },
  professional: {
    code: 'professional',
    label: 'プロフェッショナル',
    monthlyPrice: 149_800,  // 税抜価格
    userLimit: null,
    equipmentLimit: 1000,
    storageCapacityGB: 400,
    archiveDays: 365,
    featureHighlights: [
      'ユーザー数 無制限',
      '設備登録数 1,000台まで（健全度予測も1,000台まで）',
      'ストレージ容量 400GB',
      '融資準備や複数部門での運用に対応',
      '初月は日割り計算、翌月から月末定期請求',
    ],
    shortDescription: '中規模工場・融資準備企業向けプラン（初月日割り・月初請求）',
    scene: '中規模工場・融資準備',
    targetSummary: '対象: 中規模工場・融資準備企業',
    useCases: [
      '複数部門での横断的設備管理',
      '1,000台規模の設備登録と健全度予測',
    ],
  },
  enterprise: {
    code: 'enterprise',
    label: 'エンタープライズ',
    monthlyPrice: 349_800,  // 税抜価格
    userLimit: null,
    equipmentLimit: 2500,
    storageCapacityGB: 1024,
    archiveDays: 365,
    featureHighlights: [
      'ユーザー数 無制限',
      '設備登録数 2,500台まで（健全度予測も2,500台まで）',
      'ストレージ容量 1TB',
      '大規模拠点の統合運用に対応',
      '初月は日割り計算、翌月から月末定期請求',
    ],
    shortDescription: '大規模工場・M&A検討企業向けプラン（初月日割り・月初請求）',
    scene: '大規模工場・M&A検討',
    targetSummary: '対象: 大規模工場・M&A検討企業',
    useCases: [
      '全社規模での設備管理統合',
      '2,500台規模の大規模設備ポートフォリオ管理と健全度予測',
      '複数拠点の設備統合管理',
    ],
  },
  enterprise_premium: {
    code: 'enterprise_premium',
    label: 'エンタープライズ・プレミアム',
    monthlyPrice: 700_000,
    displayPriceOverride: '要相談',
    userLimit: null,
    equipmentLimit: null,
    storageCapacityGB: null,
    archiveDays: 365,
    featureHighlights: [
      'ユーザー数・設備数・ストレージ すべて無制限',
      '専任カスタマーサクセスチーム',
      '複数拠点・グループ企業への統合管理',
      '全面的なサポート体制',
      '初月は日割り計算、翌月から月末定期請求',
    ],
    shortDescription: 'ニーズに合わせた完全カスタムプラン（初月日割り・月初請求）',
    scene: '大規模複合企業・複数拠点プラント',
    targetSummary: '対象: 大規模複合企業・複数拠点プラント',
    useCases: ['カスタマイズされた設備管理ソリューション'],
  },
};

export function getSubscriptionPlanConfig(
  code: ManagedPlanCode,
): SubscriptionPlanConfig {
  return SUBSCRIPTION_PLAN_CONFIGS[code];
}

export function formatPlanMonthlyPrice(
  plan: SubscriptionPlanConfig,
  options: { withSuffix?: boolean } = {},
): string {
  if (plan.displayPriceOverride) {
    return plan.displayPriceOverride;
  }

  const formatted = currencyFormatter.format(plan.monthlyPrice);
  if (options.withSuffix) {
    return `${formatted}/月`;
  }
  return formatted;
}

export function formatPlanMonthlyPriceWithTax(
  plan: SubscriptionPlanConfig,
  options: { withSuffix?: boolean } = {},
): string {
  if (plan.displayPriceOverride) {
    return plan.displayPriceOverride;
  }

  return formatMonthlyAmountWithTax(plan.monthlyPrice, options);
}

export function formatPlanTaxIncludedLabel(
  plan: SubscriptionPlanConfig,
  options: { withSuffix?: boolean; taxRate?: number } = {},
): string {
  const taxRate = options.taxRate ?? DEFAULT_TAX_RATE;
  const suffix = options.withSuffix ? '/月' : '';
  const taxIncluded = calculateTaxIncludedAmount(plan.monthlyPrice, taxRate);

  return `税込${formatYen(taxIncluded)}${suffix}`;
}

